import {
  openDB,
  type DBSchema,
  type IDBPDatabase,
  type IDBPTransaction,
  type StoreNames,
} from 'idb';
import {
  SINGLETON_ID,
  type Challenge,
  type CleaningRoom,
  type CleaningTask,
  type DailyScoreLedger,
  type Habit,
  type HabitCategory,
  type HabitLog,
  type IsoDate,
  type ProgressState,
  type Streak,
  type TodoItem,
  type VacationAllowance,
} from '../models/types';

/**
 * IndexedDB katmanı.
 *
 * Uygulamanın TÜM kalıcı verisi cihazda, tarayıcının IndexedDB'sinde
 * durur (backend yalnızca push aboneliği ve adım verisi tutar).
 * Buradaki `...Repository` nesneleri, ekranların doğrudan IndexedDB
 * API'siyle uğraşmasını engelleyen ince bir sarmalayıcıdır.
 */

/**
 * Veritabanının şeması — hangi store'da hangi tipte kayıt olduğunu ve
 * hangi indekslerin bulunduğunu TypeScript'e anlatır. Sayesinde yanlış
 * store adı veya yanlış tipte kayıt yazmak derlemede yakalanır.
 */
interface DisciplineSchema extends DBSchema {
  habits: {
    key: string;
    value: Habit;
    indexes: { 'by-category': string };
  };
  logs: {
    key: string;
    value: HabitLog;
    indexes: { 'by-habit': string; 'by-date': string };
  };
  /** @deprecated Bkz. `Streak` tipi — Day Streak artık global tutuluyor. */
  streaks: {
    key: string;
    value: Streak;
  };
  challenges: {
    key: string;
    value: Challenge;
  };
  /** Gün gün kapanmış, değiştirilemez puan kayıtları. Anahtar = tarih. */
  dailyScoreLedger: {
    key: IsoDate;
    value: DailyScoreLedger;
  };
  cleaningRooms: {
    key: string;
    value: CleaningRoom;
  };
  cleaningTasks: {
    key: string;
    value: CleaningTask;
    indexes: { 'by-room': string };
  };
  /** Tek kayıtlı: anahtar her zaman SINGLETON_ID. */
  vacationAllowance: {
    key: string;
    value: VacationAllowance;
  };
  /** Tek kayıtlı: Başarı Barı + Day Streak sayacı. */
  progressState: {
    key: string;
    value: ProgressState;
  };
  /**
   * Yapılacaklar. Puanlamaya KATILMAZ (bkz. `TodoItem`), o yüzden
   * `logs` veya `dailyScoreLedger` ile hiçbir bağı yok.
   *
   * İndeks YOK: liste küçük olduğu için açık/tamamlanmış ayrımı
   * bellekte yapılıyor. `completedDate` üzerinde indeks kurmak ayrıca
   * tuzaklı olurdu — IndexedDB `null` değerini geçerli bir anahtar
   * saymaz, dolayısıyla açık maddeler indekse hiç girmezdi.
   */
  todos: {
    key: string;
    value: TodoItem;
  };
}

const DB_NAME = 'discipline';

/**
 * Sürüm 6: ProgressState'e `rewardDates` eklendi (§7.6 — ödül artık
 * belirli bir günü satın alıyor).
 * Bu sayı her şema değişikliğinde artırılmalı; `upgrade()` içindeki
 * `oldVersion` kontrolleri sayesinde eski cihazlardaki veri kaybolmadan
 * yeni şemaya taşınır.
 */
const DB_VERSION = 6;

/* ------------------------------------------------------------------
   Sürüm 1'den taşıma (migration) için eski kayıt biçimleri
   ------------------------------------------------------------------
   Bu tipler yalnızca `upgrade()` içinde kullanılır: veritabanındaki
   ESKİ kayıtların neye benzediğini tarif ederler. Uygulama kodunun
   geri kalanı bunları hiç görmez.
   ------------------------------------------------------------------ */

interface HabitV1 {
  id: string;
  title: string;
  category: HabitCategory;
  createdAt: string;
  archived: boolean;
}

interface HabitLogV1 {
  id: string;
  habitId: string;
  date: string;
  /** v2'de `completionRatio`'ya dönüştürüldü. */
  completed: boolean;
  note?: string;
}

interface ChallengeV1 {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  durationDays: number;
  linkedHabitId?: string;
}

/**
 * `upgrade()` sırasında açık olan özel işlemin tipi.
 * Sürüm yükseltme işlemi ('versionchange') tüm store'lara aynı anda
 * erişebilen tek işlemdir; taşıma fonksiyonu bunu parametre olarak alır.
 */
type UpgradeTransaction = IDBPTransaction<
  DisciplineSchema,
  StoreNames<DisciplineSchema>[],
  'versionchange'
>;

let dbPromise: Promise<IDBPDatabase<DisciplineSchema>> | null = null;

/**
 * Tekil (singleton) IndexedDB bağlantısını açar veya mevcut olanı
 * döndürür. Her servis kendi bağlantısını açmak yerine bunu
 * çağırmalı — aksi halde sürüm yükseltmesi kilitlenebilir.
 */
export function getDb(): Promise<IDBPDatabase<DisciplineSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<DisciplineSchema>(DB_NAME, DB_VERSION, {
      async upgrade(db, oldVersion, _newVersion, transaction) {
        // --- Sürüm 1: iskeletin ilk şeması -------------------------
        // oldVersion === 0 ise veritabanı bu cihazda hiç yoktu.
        if (oldVersion < 1) {
          const habits = db.createObjectStore('habits', { keyPath: 'id' });
          habits.createIndex('by-category', 'category');

          const logs = db.createObjectStore('logs', { keyPath: 'id' });
          logs.createIndex('by-habit', 'habitId');
          logs.createIndex('by-date', 'date');

          db.createObjectStore('streaks', { keyPath: 'habitId' });
          db.createObjectStore('challenges', { keyPath: 'id' });
        }

        // --- Sürüm 2: gamification veri modeli ---------------------
        if (oldVersion < 2) {
          // Puan defteri: anahtar doğrudan tarih olduğu için hem her
          // gün tek kayıt garanti edilir, hem de kayıtlar tarih
          // sırasında okunur (Faz 16 ve ileride dışa aktarım).
          db.createObjectStore('dailyScoreLedger', { keyPath: 'date' });

          db.createObjectStore('cleaningRooms', { keyPath: 'id' });

          const cleaningTasks = db.createObjectStore('cleaningTasks', {
            keyPath: 'id',
          });
          // Temizlik sayfası görevleri odaya göre gruplayarak
          // gösterdiği için bu indeks gerekli.
          cleaningTasks.createIndex('by-room', 'roomId');

          db.createObjectStore('vacationAllowance', { keyPath: 'id' });
          db.createObjectStore('progressState', { keyPath: 'id' });

          // Eski kayıtları yeni şemaya taşı. `oldVersion === 0` ise
          // taşınacak veri yoktur, o yüzden 1'den geliyorsak çalışır.
          if (oldVersion === 1) {
            await migrateV1ToV2(transaction);
          }
        }

        // --- Sürüm 3: otomatik alışkanlığa kaynak bilgisi ----------
        if (oldVersion < 3 && oldVersion > 0) {
          await migrateV2ToV3(transaction);
        }

        // --- Sürüm 4: Yapılacaklar listesi (Faz 14) ----------------
        // Yalnızca yeni bir store açılıyor; taşınacak eski veri yok,
        // çünkü bu özellik daha önce hiç yoktu.
        if (oldVersion < 4) {
          db.createObjectStore('todos', { keyPath: 'id' });
        }

        // --- Sürüm 5: yapılacaklara bildirim alanları (Faz 15) -----
        // Faz 14'te eklenmiş maddelerde bu alanlar yok; varsayılan
        // olarak KAPALI geliyorlar. Kullanıcının haberi olmadan
        // bildirim göndermek doğru olmazdı.
        if (oldVersion === 4) {
          await migrateV4ToV5(transaction);
        }

        // --- Sürüm 6: ödülle satın alınan günler (§7.6) ------------
        if (oldVersion > 0 && oldVersion < 6) {
          await migrateV5ToV6(transaction);
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Sürüm 1 kayıtlarını sürüm 2 şemasına taşır.
 *
 * Kural: veri ASLA silinmez, yalnızca yeni alanlar makul
 * varsayılanlarla doldurulur. Böylece iskelet sürümünü kullanmış bir
 * cihaz güncellendiğinde geçmişi kaybolmaz.
 *
 * `transaction` parametresi, `upgrade()` sırasında açık olan özel
 * sürüm-değiştirme işlemidir; yeni bir işlem açmak yerine bunu
 * kullanmak zorundayız.
 */
async function migrateV1ToV2(
  transaction: UpgradeTransaction,
): Promise<void> {
  // --- Alışkanlıklar: yeni puanlama alanlarını ekle ----------------
  const habitStore = transaction.objectStore('habits');
  // Eski kayıtlar yeni `Habit` tipine uymadığı için önce `unknown`
  // üzerinden v1 biçimine çeviriyoruz.
  const oldHabits = (await habitStore.getAll()) as unknown as HabitV1[];
  oldHabits.forEach((old, index) => {
    const migrated: Habit = {
      ...old,
      // Görsel bir varsayılan: kullanıcı Faz 9'daki formdan
      // değiştirebilecek.
      icon: 'cube',
      // Eski alışkanlıkların hepsi basit "yapıldı/yapılmadı" idi.
      kind: 'boolean',
      // CLAUDE.md §7.1'deki varsayılanlar.
      successScore: 1,
      coefficient: 1,
      // Kayıtların eski geliş sırasını koru.
      sortOrder: index,
    };
    void habitStore.put(migrated);
  });

  // --- Kayıtlar: completed (boolean) → completionRatio -------------
  const logStore = transaction.objectStore('logs');
  const oldLogs = (await logStore.getAll()) as unknown as HabitLogV1[];
  for (const old of oldLogs) {
    // `completed` alanını kaydın dışında bırakıyoruz: v2'de tek doğru
    // kaynak `completionRatio`.
    const migrated: HabitLog = {
      id: old.id,
      habitId: old.habitId,
      date: old.date,
      completionRatio: old.completed ? 1 : 0,
      // exactOptionalPropertyTypes açık olduğu için `note`'u ancak
      // gerçekten varsa ekliyoruz (undefined atamak hata verirdi).
      ...(old.note === undefined ? {} : { note: old.note }),
    };
    void logStore.put(migrated);
  }

  // --- Challenge'lar: durum ve süre birimi alanlarını ekle ---------
  const challengeStore = transaction.objectStore('challenges');
  const oldChallenges =
    (await challengeStore.getAll()) as unknown as ChallengeV1[];
  for (const old of oldChallenges) {
    const migrated: Challenge = {
      ...old,
      // Eski kayıtlarda durum yoktu; devam ediyor kabul ediyoruz.
      status: 'active',
      durationInputUnit: 'day',
      // Hangi günlerin tamamlandığı v1'de hiç tutulmuyordu.
      completedDates: [],
    };
    void challengeStore.put(migrated);
  }
}

/**
 * Sürüm 2 → 3: otomatik alışkanlıklara kaynak bilgisi ekler.
 *
 * Sürüm 2'de otomatik türden tek bir alışkanlık ("Temizlik") vardı ve
 * kod onu `kind === 'auto'` diye arıyordu. Artık her otomatik
 * alışkanlık, durumunu hangi kuralın hesapladığını kendisi söylüyor;
 * mevcut kayıtları da temizlik kuralına bağlıyoruz.
 */
async function migrateV2ToV3(transaction: UpgradeTransaction): Promise<void> {
  const habitStore = transaction.objectStore('habits');
  const habits = await habitStore.getAll();

  for (const habit of habits) {
    if (habit.kind !== 'auto' || habit.autoSource !== undefined) continue;
    void habitStore.put({ ...habit, autoSource: 'cleaning' });
  }
}

/**
 * Sürüm 4 → 5: yapılacak maddelerine bildirim alanları ekler.
 *
 * Faz 14'te oluşturulmuş maddelerde bu alanlar hiç yok. Varsayılan
 * KAPALI: kullanıcı hiçbir ayar yapmadan bildirim almaya başlamamalı.
 */
async function migrateV4ToV5(transaction: UpgradeTransaction): Promise<void> {
  const todoStore = transaction.objectStore('todos');
  const todos = await todoStore.getAll();

  for (const todo of todos) {
    // Alanlar zaten varsa dokunma — taşıma tekrar çalışırsa
    // kullanıcının ayarlarını silmesin.
    if (typeof todo.notificationsEnabled === 'boolean') continue;
    void todoStore.put({
      ...todo,
      notificationsEnabled: false,
      notificationTimes: [],
    });
  }
}

/**
 * Sürüm 5 → 6: `ProgressState`'e `rewardDates` ekler.
 *
 * §7.6 değişti: ödül artık sayaca körlemesine +1 eklemiyor, BELİRLİ
 * BİR GÜNÜ satın alıyor. Eski kayıtlarda bu liste yok; boş başlıyor.
 *
 * Geçmişte kullanılmış ödüller geriye dönük olarak bir güne
 * bağlanamaz — hangi gün kullanıldıkları hiç kaydedilmemişti. Zaten
 * kazandırdıkları seri günleri sayaca işlenmiş durumda, dolayısıyla
 * kullanıcı bir şey kaybetmiyor.
 */
async function migrateV5ToV6(transaction: UpgradeTransaction): Promise<void> {
  const store = transaction.objectStore('progressState');
  const states = await store.getAll();

  for (const state of states) {
    // Alan zaten varsa dokunma — taşıma tekrar çalışırsa kayıtları
    // silmesin.
    if (Array.isArray(state.rewardDates)) continue;
    void store.put({ ...state, rewardDates: [] });
  }
}

/* ==================================================================
   Repository'ler
   ------------------------------------------------------------------
   Her store için list / upsert / remove üçlüsü. Ekranlar ve servisler
   IndexedDB'ye yalnızca buradan erişir.
   ================================================================== */

export const habitRepository = {
  /** Arşivlenmişler dahil TÜM alışkanlıklar (yönetim ekranı için). */
  async list(): Promise<Habit[]> {
    const db = await getDb();
    const all = await db.getAll('habits');
    // Kullanıcının belirlediği sırayı koru (IndexedDB anahtar
    // sırasında, yani UUID'ye göre rastgele döndürür).
    return all.sort((a, b) => a.sortOrder - b.sortOrder);
  },
  /** Yalnızca aktif alışkanlıklar — Anasayfa ve puanlama bunu kullanır. */
  async listActive(): Promise<Habit[]> {
    const all = await this.list();
    return all.filter((habit) => !habit.archived);
  },
  /** Arşivlenmiş alışkanlıklar — yönetim ekranında geri getirilebilirler. */
  async listArchived(): Promise<Habit[]> {
    const all = await this.list();
    return all.filter((habit) => habit.archived);
  },
  async get(id: string): Promise<Habit | undefined> {
    const db = await getDb();
    return db.get('habits', id);
  },
  async upsert(habit: Habit): Promise<void> {
    const db = await getDb();
    await db.put('habits', habit);
  },
  /**
   * Yumuşak silme: kaydı `archived: true` yapar, ASLA gerçekten
   * silmez. Geçmiş `dailyScoreLedger` kayıtları bu alışkanlığın
   * katkısıyla hesaplandığı ve §7.2 gereği yeniden hesaplanamayacağı
   * için, kaydın kendisi durmak zorunda (Faz 9).
   */
  async archive(id: string): Promise<void> {
    const db = await getDb();
    const habit = await db.get('habits', id);
    if (!habit) return;
    await db.put('habits', { ...habit, archived: true });
  },
  /**
   * Arşivlenmiş bir alışkanlığı listeye geri getirir.
   *
   * Geçmişi değiştirmez: kapanmış günlerin puan kayıtları §7.2 gereği
   * dokunulmazdır, alışkanlık yalnızca BUGÜNDEN itibaren yeniden
   * hesaba katılır.
   */
  async restore(id: string): Promise<void> {
    const db = await getDb();
    const habit = await db.get('habits', id);
    if (!habit) return;
    await db.put('habits', { ...habit, archived: false });
  },
};

export const logRepository = {
  async listByDate(date: IsoDate): Promise<HabitLog[]> {
    const db = await getDb();
    return db.getAllFromIndex('logs', 'by-date', date);
  },
  async listByHabit(habitId: string): Promise<HabitLog[]> {
    const db = await getDb();
    return db.getAllFromIndex('logs', 'by-habit', habitId);
  },
  async upsert(log: HabitLog): Promise<void> {
    const db = await getDb();
    await db.put('logs', log);
  },
  async remove(id: string): Promise<void> {
    const db = await getDb();
    await db.delete('logs', id);
  },
};

export const challengeRepository = {
  async list(): Promise<Challenge[]> {
    const db = await getDb();
    return db.getAll('challenges');
  },
  /**
   * Devam eden challenge. §7.4 gereği aynı anda en fazla 1 tane
   * olabilir; birden fazla bulunursa (olmamalı) ilki döner.
   */
  async getActive(): Promise<Challenge | undefined> {
    const all = await this.list();
    return all.find((challenge) => challenge.status === 'active');
  },
  async upsert(challenge: Challenge): Promise<void> {
    const db = await getDb();
    await db.put('challenges', challenge);
  },
  async remove(id: string): Promise<void> {
    const db = await getDb();
    await db.delete('challenges', id);
  },
};

export const dailyScoreLedgerRepository = {
  /** Tüm kayıtlar, tarih sırasında (eskiden yeniye). */
  async list(): Promise<DailyScoreLedger[]> {
    const db = await getDb();
    // Anahtar tarih olduğu için IndexedDB zaten tarih sırasında verir.
    return db.getAll('dailyScoreLedger');
  },
  async get(date: IsoDate): Promise<DailyScoreLedger | undefined> {
    const db = await getDb();
    return db.get('dailyScoreLedger', date);
  },
  /**
   * Bir günün kaydını YAZAR — ve o gün için kayıt zaten varsa hata
   * fırlatır.
   *
   * Bu bilinçli bir katılık: §7.2'ye göre kapanmış bir gün
   * değiştirilemez. `upsert` yerine `append` sunarak, ileride yanlışlıkla
   * yazılan bir "yeniden hesaplama" kodunun geçmişi bozmasını veri
   * katmanında engelliyoruz.
   */
  async append(entry: DailyScoreLedger): Promise<void> {
    const db = await getDb();
    const existing = await db.get('dailyScoreLedger', entry.date);
    if (existing) {
      throw new Error(
        `${entry.date} için puan kaydı zaten var; geçmiş kayıtlar değiştirilemez.`,
      );
    }
    await db.put('dailyScoreLedger', entry);
  },
};

export const cleaningRoomRepository = {
  async list(): Promise<CleaningRoom[]> {
    const db = await getDb();
    const all = await db.getAll('cleaningRooms');
    return all.sort((a, b) => a.sortOrder - b.sortOrder);
  },
  async upsert(room: CleaningRoom): Promise<void> {
    const db = await getDb();
    await db.put('cleaningRooms', room);
  },
  /**
   * Odayı ve içindeki tüm görevleri siler.
   * Temizlik verisi puan defterine doğrudan girmediği (yalnızca §7.5'teki
   * otomatik alışkanlığı besleyip orada tam sayıya dönüştüğü) için burada
   * gerçek silme güvenlidir.
   */
  async remove(id: string): Promise<void> {
    const db = await getDb();
    const tasks = await db.getAllFromIndex('cleaningTasks', 'by-room', id);
    // Oda ve görevleri tek bir işlemde silinsin ki yarıda kalıp
    // sahipsiz görev bırakmasın.
    const tx = db.transaction(['cleaningRooms', 'cleaningTasks'], 'readwrite');
    await Promise.all([
      tx.objectStore('cleaningRooms').delete(id),
      ...tasks.map((task) => tx.objectStore('cleaningTasks').delete(task.id)),
      tx.done,
    ]);
  },
};

export const cleaningTaskRepository = {
  async list(): Promise<CleaningTask[]> {
    const db = await getDb();
    const all = await db.getAll('cleaningTasks');
    return all.sort((a, b) => a.sortOrder - b.sortOrder);
  },
  async listByRoom(roomId: string): Promise<CleaningTask[]> {
    const db = await getDb();
    const all = await db.getAllFromIndex('cleaningTasks', 'by-room', roomId);
    return all.sort((a, b) => a.sortOrder - b.sortOrder);
  },
  async upsert(task: CleaningTask): Promise<void> {
    const db = await getDb();
    await db.put('cleaningTasks', task);
  },
  async remove(id: string): Promise<void> {
    const db = await getDb();
    await db.delete('cleaningTasks', id);
  },
};

export const vacationAllowanceRepository = {
  /** Tek kayıt; hiç tatil hakkı açılmadıysa `undefined`. */
  async get(): Promise<VacationAllowance | undefined> {
    const db = await getDb();
    return db.get('vacationAllowance', SINGLETON_ID);
  },
  async upsert(allowance: VacationAllowance): Promise<void> {
    const db = await getDb();
    await db.put('vacationAllowance', allowance);
  },
  async remove(): Promise<void> {
    const db = await getDb();
    await db.delete('vacationAllowance', SINGLETON_ID);
  },
};

export const progressStateRepository = {
  /** Tek kayıt; uygulama hiç başlatılmadıysa `undefined`. */
  async get(): Promise<ProgressState | undefined> {
    const db = await getDb();
    return db.get('progressState', SINGLETON_ID);
  },
  async upsert(state: ProgressState): Promise<void> {
    const db = await getDb();
    await db.put('progressState', state);
  },
  async remove(): Promise<void> {
    const db = await getDb();
    await db.delete('progressState', SINGLETON_ID);
  },
};

export const todoRepository = {
  /** Tüm maddeler — açık/tamamlanmış ayrımı yapılmadan. */
  async list(): Promise<TodoItem[]> {
    const db = await getDb();
    return db.getAll('todos');
  },
  /** Henüz tamamlanmamış maddeler, kullanıcının belirlediği sırada. */
  async listOpen(): Promise<TodoItem[]> {
    const all = await todoRepository.list();
    return all
      .filter((item) => item.completedDate === null)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  },
  /**
   * Tamamlanmış maddeler, en son tamamlanan en üstte.
   * Aynı gün tamamlananlar arasında sıra garanti edilmez — gün
   * bazında çalıştığımız için (§ `IsoDate`) daha ince bir zaman
   * bilgisi tutmuyoruz.
   */
  async listCompleted(): Promise<TodoItem[]> {
    const all = await todoRepository.list();
    // Tarih alanını filtrede daraltıp yerel bir değişkene alıyoruz;
    // böylece sıralamada `!` (kesin var) demek zorunda kalmıyoruz.
    const completed = all.flatMap((item) =>
      item.completedDate === null ? [] : [{ item, date: item.completedDate }],
    );
    return completed
      .sort((a, b) => (a.date < b.date ? 1 : -1))
      .map((entry) => entry.item);
  },
  async get(id: string): Promise<TodoItem | undefined> {
    const db = await getDb();
    return db.get('todos', id);
  },
  async upsert(item: TodoItem): Promise<void> {
    const db = await getDb();
    await db.put('todos', item);
  },
  async remove(id: string): Promise<void> {
    const db = await getDb();
    await db.delete('todos', id);
  },
};
